import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Scissors, Clock, Loader2 } from "lucide-react";

export default function AdminServices() {
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    name_en: "",
    description: "",
    price: "",
    duration_minutes: ""
  });
  const [saving, setSaving] = useState(false);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data);
    } catch (error) {
      toast.error("Hizmetler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, [token]);

  const openAddDialog = () => {
    setEditingService(null);
    setFormData({
      name: "",
      name_en: "",
      description: "",
      price: "",
      duration_minutes: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      name_en: service.name_en || "",
      description: service.description || "",
      price: service.price.toString(),
      duration_minutes: service.duration_minutes.toString()
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.price || !formData.duration_minutes) {
      toast.error("Ad, fiyat ve süre alanları zorunludur");
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        duration_minutes: parseInt(formData.duration_minutes)
      };

      if (editingService) {
        await axios.put(
          `${API}/services/${editingService.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Hizmet güncellendi");
      } else {
        await axios.post(
          `${API}/services`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Hizmet eklendi");
      }
      setDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error("İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu hizmeti silmek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/services/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Hizmet silindi");
      fetchServices();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const toggleActive = async (service) => {
    try {
      await axios.put(
        `${API}/services/${service.id}`,
        { is_active: !service.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(service.is_active ? "Hizmet pasif yapıldı" : "Hizmet aktif yapıldı");
      fetchServices();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  return (
    <AdminLayout title="Hizmet Yönetimi">
      <div data-testid="admin-services">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-[#6b7280]">{services.length} hizmet kayıtlı</p>
          <Button onClick={openAddDialog} className="btn-primary" data-testid="add-service-btn">
            <Plus className="w-4 h-4 mr-2" />
            Hizmet Ekle
          </Button>
        </div>

        {/* Services Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="bg-white border border-[#e5e7eb] rounded-xl overflow-x-auto shadow-sm">
            <table className="table-elite">
              <thead>
                <tr>
                  <th>Hizmet</th>
                  <th>Açıklama</th>
                  <th>Süre</th>
                  <th>Fiyat</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {services.map((service) => (
                  <tr key={service.id} className={!service.is_active ? "opacity-50" : ""} data-testid={`service-row-${service.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#c9a96e]/20 rounded-lg flex items-center justify-center">
                          <Scissors className="w-5 h-5 text-[#c9a96e]" />
                        </div>
                        <div>
                          <p className="text-[#1a1a2e] font-medium">{service.name}</p>
                          {service.name_en && <p className="text-[#9ca3af] text-sm">{service.name_en}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="text-[#6b7280] max-w-xs truncate">{service.description || "—"}</td>
                    <td>
                      <div className="flex items-center gap-1 text-[#6b7280]">
                        <Clock className="w-4 h-4" />
                        {service.duration_minutes} dk
                      </div>
                    </td>
                    <td className="text-[#c9a96e] font-accent text-lg">{service.price}₺</td>
                    <td>
                      <span className={`status-badge ${service.is_active ? "status-approved" : "status-cancelled"}`}>
                        {service.is_active ? "Aktif" : "Pasif"}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => openEditDialog(service)}
                          variant="ghost"
                          size="sm"
                          className="text-[#6b7280] hover:text-[#1a1a2e]"
                          data-testid={`edit-service-${service.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => toggleActive(service)}
                          variant="ghost"
                          size="sm"
                          className={service.is_active ? "text-amber-500" : "text-emerald-600"}
                        >
                          {service.is_active ? "Pasif" : "Aktif"}
                        </Button>
                        <Button
                          onClick={() => handleDelete(service.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-600"
                          data-testid={`delete-service-${service.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white border-[#e5e7eb] text-[#1a1a2e]">
            <DialogHeader>
              <DialogTitle className="font-display">
                {editingService ? "Hizmet Düzenle" : "Yeni Hizmet Ekle"}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="font-accent text-xs text-[#6b7280] mb-2 block">HİZMET ADI (TR) *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="input-elite"
                  placeholder="Örn: Saç Kesimi"
                  data-testid="service-name-input"
                />
              </div>
              
              <div>
                <label className="font-accent text-xs text-[#6b7280] mb-2 block">HİZMET ADI (EN)</label>
                <Input
                  value={formData.name_en}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                  className="input-elite"
                  placeholder="Örn: Haircut"
                />
              </div>
              
              <div>
                <label className="font-accent text-xs text-[#6b7280] mb-2 block">AÇIKLAMA</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full h-20 input-elite resize-none"
                  placeholder="Hizmet açıklaması"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-accent text-xs text-[#6b7280] mb-2 block">FİYAT (₺) *</label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    className="input-elite"
                    placeholder="150"
                    min="0"
                    data-testid="service-price-input"
                  />
                </div>
                
                <div>
                  <label className="font-accent text-xs text-[#6b7280] mb-2 block">SÜRE (dk) *</label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: e.target.value }))}
                    className="input-elite"
                    placeholder="30"
                    min="5"
                    data-testid="service-duration-input"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#6b7280]">
                  İptal
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-service-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
